import NiceModal, { useModal } from "@ebay/nice-modal-react"
import { ChevronLeft, Monitor, RefreshCw, Terminal } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect, useRef, useState } from "react"
import { useComputers } from "../../computers/context"
import type { RuntimeRecord } from "../../nest/generated"
import { useCodeNavigate } from "../../routing"
import { Modal } from "../ui/Modal"
import { ProcessDetails, ProcessStatus, processButtonClass } from "./ProcessDetails"
import { ProcessFleetModel } from "./ProcessFleetModel"
import { ProcessPortLink } from "./ProcessPortLink"
import { ProcessTaskLink, type ProcessTaskNavigation, type ProcessTaskTarget } from "./ProcessTaskLink"

interface ProcessTask {
    nodeId: string
    taskId: string
}

export const ProcessesModal = NiceModal.create(({ currentTask }: { currentTask?: ProcessTask }) => {
    const computers = useComputers()
    const navigate = useCodeNavigate()
    const modal = useModal()
    const opening = useRef(false)
    const [openingTask, setOpeningTask] = useState(false)
    const [taskError, setTaskError] = useState("")
    const [model, setModel] = useState<ProcessFleetModel | null>(null)
    useEffect(() => {
        const fleet = new ProcessFleetModel(computers.backend, computers.state.computers)
        setModel(fleet)
        void fleet.refresh()
        return () => fleet.dispose()
    }, [computers.backend, computers.state.computers])
    const openTask = async (target: ProcessTaskTarget) => {
        if (opening.current) return
        opening.current = true
        setOpeningTask(true)
        setTaskError("")
        try {
            const computer = computers.state.computers.find((entry) => entry.nodeId === target.nodeId)
            if (!computer) throw new Error("The task's computer is no longer registered.")
            if (target.nodeId !== computers.state.activeNodeId && !(await computers.selectScope(target.nodeId))) {
                throw new Error(`Could not open ${computer.name}. Reconnect and try again.`)
            }
            navigate.go("CodeWorkspaceTask", { workspaceId: target.workspaceId, taskId: target.taskId })
            modal.remove()
        } catch (error) {
            setTaskError(error instanceof Error ? error.message : String(error))
        } finally {
            opening.current = false
            setOpeningTask(false)
        }
    }
    return (
        <Modal title="Processes" size="xl" layout="panel" closeDisabled={openingTask}>
            {taskError ? (
                <p role="alert" className="border-b border-border px-3 py-2 text-xs text-error">
                    {taskError}
                </p>
            ) : null}
            {model ? (
                <ProcessesPanel model={model} currentTask={currentTask} taskNavigation={{ open: (target) => void openTask(target), busy: openingTask }} />
            ) : (
                <p className="p-3 text-xs text-muted">Connecting…</p>
            )}
        </Modal>
    )
})

interface ProcessesPanelProps {
    model: ProcessFleetModel
    currentTask?: ProcessTask
    taskNavigation: ProcessTaskNavigation
}

export const ProcessesPanel = observer(({ model, currentTask, taskNavigation }: ProcessesPanelProps) => {
    const [selection, setSelection] = useState<{ nodeId: string; process: RuntimeRecord } | null>(null)
    const [mobileDetailOpen, setMobileDetailOpen] = useState(false)
    const selectedComputer = model.computers.find((entry) => entry.computer.nodeId === selection?.nodeId)
    const selectedClient = selectedComputer?.status === "ready" ? model.clientFor(selectedComputer.computer.nodeId) : null
    const belongsToCurrentTask = (process: RuntimeRecord, nodeId: string) =>
        Boolean(
            currentTask &&
            process.labels?.["wildlark.taskId"] === currentTask.taskId &&
            (process.labels["wildlark.originNodeId"] || nodeId) === currentTask.nodeId
        )
    const groups = model.computers
        .map((entry) => ({
            ...entry,
            processes: [...entry.processes].sort(
                (left, right) => Number(belongsToCurrentTask(right, entry.computer.nodeId)) - Number(belongsToCurrentTask(left, entry.computer.nodeId))
            ),
        }))
        .sort(
            (left, right) =>
                Number(right.processes.some((process) => belongsToCurrentTask(process, right.computer.nodeId))) -
                Number(left.processes.some((process) => belongsToCurrentTask(process, left.computer.nodeId)))
        )
    const connectedCount = model.computers.filter((entry) => entry.status === "ready").length
    const unavailableCount = model.computers.filter((entry) => entry.status === "error").length
    const firstGroup = groups.find((entry) => entry.status === "ready" && entry.processes.length > 0)
    const firstProcess = firstGroup?.processes[0]
    const firstNodeId = firstGroup?.computer.nodeId
    // Open the preferred process's logs after the first complete fleet read.
    // On phones the list stays visible until the user chooses a row.
    useEffect(() => {
        if (!selection && !model.loading && firstNodeId && firstProcess) setSelection({ nodeId: firstNodeId, process: firstProcess })
    }, [selection, model.loading, firstNodeId, firstProcess])

    return (
        <div className="grid h-[min(70dvh,640px)] min-h-0 text-base-content md:grid-cols-[240px_minmax(0,1fr)]">
            <div className={`min-h-0 flex-col border-border md:flex md:border-r ${mobileDetailOpen ? "hidden" : "flex"}`}>
                <div className="flex shrink-0 items-center gap-2 border-b border-border px-2 py-1">
                    <span className="min-w-0 flex-1 text-[11px] text-muted">All computers</span>
                    <select
                        aria-label="Process status"
                        className="h-7 min-w-0 bg-base-100 text-xs text-base-content outline-none focus-visible:ring-1 focus-visible:ring-primary"
                        value={model.status}
                        onChange={(event) => model.setFilters(event.target.value === "active" ? "active" : "all", model.namespace)}
                    >
                        <option value="active">Running</option>
                        <option value="all">All processes</option>
                    </select>
                    <button
                        type="button"
                        className={processButtonClass}
                        aria-label="Refresh processes"
                        title="Refresh processes"
                        disabled={model.loading}
                        onClick={() => void model.refresh()}
                    >
                        <RefreshCw size={13} className={model.loading ? "animate-spin" : undefined} />
                    </button>
                </div>
                <div className="shrink-0 border-b border-border px-2 py-1">
                    <input
                        aria-label="Namespace"
                        placeholder="Filter namespace…"
                        className="input h-7 w-full min-w-0 bg-transparent px-1 text-xs text-base-content outline-none placeholder:text-muted focus-visible:ring-1 focus-visible:ring-primary"
                        value={model.namespace}
                        onChange={(event) => model.setFilters(model.status, event.target.value)}
                    />
                </div>
                <div className="min-h-0 flex-1 overflow-auto">
                    {model.computers.length === 0 ? <p className="p-3 text-xs text-muted">No registered computers.</p> : null}
                    {groups.map((entry) => (
                        <section key={entry.computer.nodeId} aria-label={`${entry.computer.name} processes`}>
                            <div className="sticky top-0 z-10 flex items-center gap-1.5 border-b border-border/50 bg-base-200 px-3 py-1.5">
                                <Monitor size={11} className="shrink-0 text-muted" />
                                <h3 className="min-w-0 flex-1 truncate text-[10px] font-medium text-muted" title={entry.computer.nodeId}>
                                    {entry.computer.name}
                                </h3>
                                <span className={`text-[10px] ${entry.status === "error" ? "text-warning" : "text-muted"}`}>
                                    {entry.status === "error"
                                        ? "Unavailable"
                                        : entry.status === "waiting"
                                          ? "Connecting…"
                                          : `${entry.processes.length}${entry.nextCursor ? "+" : ""}`}
                                </span>
                            </div>
                            {entry.error ? (
                                <div className="px-3 py-2 text-[11px] text-error">
                                    <p role="alert" className="break-words">
                                        {entry.error}
                                    </p>
                                    {entry.processes.length ? <p className="text-muted">Last known processes</p> : null}
                                </div>
                            ) : null}
                            {entry.status === "ready" && entry.processes.length === 0 ? (
                                <p className="px-3 py-2 text-[11px] text-muted">No matching processes</p>
                            ) : null}
                            {entry.processes.map((process) => {
                                const selected = selection?.nodeId === entry.computer.nodeId && selection.process.runtimeId === process.runtimeId
                                return (
                                    <div
                                        key={process.runtimeId}
                                        className={`border-l-2 pr-1 ${selected ? "border-primary bg-primary/5" : "border-transparent"}`}
                                    >
                                        <div className="flex items-center">
                                            <button
                                                type="button"
                                                aria-pressed={selected}
                                                className="btn block min-w-0 flex-1 px-2.5 py-1.5 text-left text-base-content transition-colors hover:bg-base-200 focus-visible:outline focus-visible:outline-primary"
                                                title={[process.commandPreview, process.cwd].filter(Boolean).join("\n")}
                                                onClick={() => {
                                                    setSelection({ nodeId: entry.computer.nodeId, process })
                                                    setMobileDetailOpen(true)
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className="min-w-0 flex-1 truncate text-xs font-medium">
                                                        {process.name || process.label || process.runtimeId}
                                                    </span>
                                                    <ProcessStatus process={process} compact />
                                                </div>
                                            </button>
                                            <ProcessPortLink
                                                computer={entry.computer}
                                                port={process.port}
                                                name={process.name || process.label || process.runtimeId}
                                            />
                                        </div>
                                        <div className="flex items-center gap-2 px-2.5 pb-1.5 text-[10px]">
                                            <span className="min-w-0 flex-1 truncate text-muted" title={process.namespace || process.cwd}>
                                                {process.namespace || process.cwd || "Unnamespaced"}
                                            </span>
                                            <div className="min-w-0 max-w-[65%]">
                                                <ProcessTaskLink
                                                    process={process}
                                                    nodeId={entry.computer.nodeId}
                                                    current={belongsToCurrentTask(process, entry.computer.nodeId)}
                                                    navigation={taskNavigation}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                            {entry.cursor || entry.nextCursor ? (
                                <div className="flex items-center justify-between px-2 py-1">
                                    {entry.cursor ? (
                                        <button
                                            type="button"
                                            className="btn px-1 py-1 text-[11px] text-muted hover:text-base-content"
                                            onClick={() => model.setPage(entry.computer.nodeId)}
                                        >
                                            First page
                                        </button>
                                    ) : null}
                                    {entry.nextCursor ? (
                                        <button
                                            type="button"
                                            className="btn ml-auto px-1 py-1 text-[11px] text-muted hover:text-base-content"
                                            onClick={() => model.setPage(entry.computer.nodeId, entry.nextCursor)}
                                        >
                                            Next page →
                                        </button>
                                    ) : null}
                                </div>
                            ) : null}
                        </section>
                    ))}
                </div>
                {model.batch ? (
                    <details className="max-h-32 shrink-0 overflow-auto border-t border-border px-3 py-2 text-[10px] text-muted">
                        <summary className={`cursor-pointer ${unavailableCount ? "text-warning" : ""}`}>
                            {connectedCount}/{model.computers.length} computers
                            {unavailableCount ? ` · ${unavailableCount} unavailable` : model.loading && !connectedCount ? " connecting" : " connected"}
                        </summary>
                        <div className="pt-1.5" role="status">
                            {model.batch.requested} requested · {model.batch.succeeded} succeeded · {model.batch.failed} failed
                            {model.batch.firstErrors.map((error) => (
                                <p key={error.nodeId} className="mt-1 break-words">
                                    {error.message}
                                </p>
                            ))}
                        </div>
                    </details>
                ) : null}
            </div>
            <div className={`min-h-0 min-w-0 flex-col md:flex ${mobileDetailOpen ? "flex" : "hidden"}`}>
                {selection && selectedComputer ? (
                    selectedClient ? (
                        <ProcessDetails
                            key={JSON.stringify([selection.nodeId, selection.process.runtimeId])}
                            client={selectedClient}
                            computer={selectedComputer.computer}
                            initial={selection.process}
                            onChange={() => void model.refresh()}
                            onBack={() => setMobileDetailOpen(false)}
                            taskNavigation={taskNavigation}
                        />
                    ) : (
                        <div className="flex h-full flex-col">
                            <button
                                type="button"
                                className="btn flex items-center gap-1 border-b border-border px-3 py-2 text-xs text-muted md:hidden"
                                onClick={() => setMobileDetailOpen(false)}
                            >
                                <ChevronLeft size={14} /> All computers
                            </button>
                            <p className="p-4 text-xs text-muted">
                                {selectedComputer.computer.name} is unavailable. Refresh to reconnect and manage its processes.
                            </p>
                        </div>
                    )
                ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-xs text-muted">
                        <Terminal size={20} strokeWidth={1.5} />
                        <span>{model.loading ? "Loading processes…" : "Select a process to view logs"}</span>
                    </div>
                )}
            </div>
        </div>
    )
})
